import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { Input, Label, FieldError } from '@/components/ui/Form';
import { Card } from '@/components/ui/Card';
import { useAuthStore } from '@/lib/auth.store';
import { ApiError } from '@/lib/api';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: string } };
  const loginWithPassword = useAuthStore((s) => s.loginWithPassword);
  const loading = useAuthStore((s) => s.loading);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [errCode, setErrCode] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setErrCode(null);
    try {
      await loginWithPassword(email.trim().toLowerCase(), password);
      navigate(location.state?.from ?? '/chart', { replace: true });
    } catch (err) {
      setError((err as Error).message);
      if (err instanceof ApiError) setErrCode(err.code ?? null);
    }
  };

  return (
    <main className="flex flex-1 items-center justify-center px-4 py-8 sm:px-6 sm:py-12">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <h1 className="font-serif text-3xl text-fg text-center mb-2">登录观微</h1>
        <p className="text-sm text-fg-muted text-center mb-8">
          {/* theme-token-disable-next-line */}
          观天地之微，察人事之要
        </p>

        <Card className="p-8">
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="email" required>
                邮箱
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>

            <div>
              <Label htmlFor="password" required>
                密码
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                minLength={8}
                maxLength={72}
                required
              />
            </div>

            <FieldError>
              {error}
              {errCode === 'PASSWORD_NOT_SET' && (
                <>
                  {' '}
                  <Link
                    to="/auth/set-password"
                    state={{ from: location.state?.from }}
                    className="underline text-accent"
                  >
                    去设置密码
                  </Link>
                </>
              )}
            </FieldError>

            <Button type="submit" size="lg" className="w-full" disabled={loading}>
              {loading ? '登录中…' : '登录'}
            </Button>
          </form>

          <div className="mt-6 flex justify-between text-sm text-fg-2">
            <Link
              to="/auth/register"
              state={{ from: location.state?.from }}
              className="hover:text-accent"
            >
              注册新账号
            </Link>
            <Link
              to="/auth/reset-password"
              state={{ from: location.state?.from }}
              className="hover:text-accent"
            >
              忘记密码？
            </Link>
          </div>
        </Card>
      </motion.div>
    </main>
  );
}
